import SecureStorage from "react-secure-storage";
import { useAuthStore } from "../store/useAuthStore";
import { useUserStore } from "../store/useUserStore";
import { useNotificationStore } from "../store/useNotificationStore";

// Non-sensitive UI state that may survive a logout. Keeping the chosen portal
// lets /logged-out and its "Back to login" action resolve to the right admin
// portal after the session is gone.
const PRESERVED_KEYS = ["selectedPortal"];

/**
 * True from the moment a deliberate sign-out starts until the browser leaves
 * for the Cognito hosted logout.
 *
 * A deliberate logout owns the teardown and the single navigation that follows
 * it. Anything else that hard-navigates in the meantime — notably the 401
 * response interceptor, which fires on any background poll that lands while the
 * sign-out is in flight — would abort the uncommitted navigation to the Cognito
 * hosted logout and leave the hosted session alive (ICE-BC-BUG-018).
 *
 * Deliberately a module-level flag rather than storage: `clearClientSession()`
 * wipes localStorage/sessionStorage, so a stored flag would erase itself
 * partway through the very sequence it exists to protect.
 */
let signOutInFlight = false;

/** Marks the start of a deliberate sign-out. See {@link signOutIsInFlight}. */
export function beginSignOut() {
  signOutInFlight = true;
}

/** Clears the sign-out guard. Only needed if a sign-out is ever abandoned. */
export function endSignOut() {
  signOutInFlight = false;
}

/** Whether a deliberate sign-out currently owns the navigation. */
export function signOutIsInFlight() {
  return signOutInFlight;
}

/**
 * Clear every trace of the signed-in session.
 *
 * The session lives in more places than the auth store: SecureStorage holds the
 * access/refresh/session tokens that api.ts attaches as the Bearer token on
 * every request, and `auth-storage`/`user-store` are persisted zustand stores
 * that outlive a page reload. Clearing only some of them left the previous
 * session usable after logout, so both the logout action and the 401 handler
 * go through here rather than each clearing their own subset.
 */
export function clearClientSession() {
  const preserved: Record<string, string> = {};
  try {
    PRESERVED_KEYS.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        preserved[key] = value;
      }
    });
  } catch {
    /* storage unavailable */
  }

  try {
    useAuthStore.getState().logout();
  } catch {
    /* ignore */
  }
  try {
    useNotificationStore.getState().clearNotifications();
  } catch {
    /* ignore */
  }
  try {
    useAuthStore.persist.clearStorage();
  } catch {
    /* ignore */
  }
  try {
    useUserStore.persist.clearStorage();
  } catch {
    /* ignore */
  }
  // access_token / refresh_token / session_token
  try {
    SecureStorage.clear();
  } catch {
    /* ignore */
  }

  // Catch-all for the remaining session keys written directly (access_token,
  // userRole, username) so nothing from the old session is left behind.
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    /* ignore */
  }

  try {
    Object.entries(preserved).forEach(([key, value]) =>
      localStorage.setItem(key, value),
    );
  } catch {
    /* ignore */
  }
}
